import {
    Badge,
    Button,
    Card,
    Form,
    Input,
    Message,
    Notification,
    Space,
    Tabs,
    Tag,
    Tooltip
} from "@arco-design/web-react";
import { IconCopy, IconEye, IconEyeInvisible, IconUndo } from "@arco-design/web-react/icon";
import copy from "copy-to-clipboard";
import { useCallback, useEffect, useRef, useState } from "react";
import { SettingAPI } from "@/apis/system";
import { SettingCategory, SettingCategoryDef } from "@/enums/system";
import { buildEnumOptions } from "@/enums/utils";

const FormItem = Form.Item;
const FormList = Form.List;
const TabPane = Tabs.TabPane;

const SECRET_NAME_PATTERN = /password|secret/i;
const MONOSPACE_FONT = "SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace";
const OTHER_GROUP_KEY = "other";

const groupKeyOf = (category) => (category == null ? OTHER_GROUP_KEY : `cat-${category}`);

export const Settings = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState({});
    const [activeTab, setActiveTab] = useState();
    const [revealed, setRevealed] = useState({});
    const savedValues = useRef({});

    const hasDirty = Object.values(dirty).some(Boolean);

    const refreshDirty = useCallback(() => {
        const settings = form.getFieldValue("settings") || [];
        const next = {};
        settings.forEach(setting => {
            if (!setting) return;
            next[setting.id] = (setting.settingValue ?? "") !== (savedValues.current[setting.id] ?? "");
        });
        setDirty(next);
    }, [form]);

    const getSettings = useCallback(async () => {
        try {
            const settings = await SettingAPI.getSettings();
            form.setFieldsValue({ settings });
            savedValues.current = Object.fromEntries(settings.map(setting => [setting.id, setting.settingValue]));
            setActiveTab(groupKeyOf(settings[0]?.category));
            refreshDirty();
        } finally {
            setLoading(false);
        }
    }, [form, refreshDirty]);

    useEffect(() => {
        getSettings();
    }, [getSettings]);

    // Warn before closing/reloading the page with unsaved changes
    useEffect(() => {
        if (!hasDirty) return;
        const handler = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [hasDirty]);

    const save = async () => {
        const changed = (form.getFieldValue("settings") || []).filter(setting => dirty[setting.id]);
        if (changed.length === 0) return;
        try {
            setSaving(true);
            await SettingAPI.save(changed);
            changed.forEach(setting => {
                savedValues.current[setting.id] = setting.settingValue;
            });
            Message.success("Settings saved successfully");
            refreshDirty();
        } catch (error) {
            Message.error(error);
        } finally {
            setSaving(false);
        }
    };

    const toggleReveal = (id) => {
        setRevealed(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const resetField = (fieldPath, id) => {
        form.setFieldValue(`${fieldPath}.settingValue`, savedValues.current[id] ?? "");
        refreshDirty();
    };

    const resetAll = () => {
        const settings = form.getFieldValue("settings") || [];
        form.setFieldsValue({
            settings: settings.map(setting => ({ ...setting, settingValue: savedValues.current[setting.id] ?? "" }))
        });
        refreshDirty();
    };

    const buildGroups = (fields) => {
        const groups = buildEnumOptions(SettingCategoryDef).map(category => ({
            key: groupKeyOf(category.value),
            label: category.label,
            fields: []
        }));
        const other = { key: OTHER_GROUP_KEY, label: "Other", fields: [] };
        fields.forEach(item => {
            const category = form.getFieldValue(`${item.field}.category`);
            const group = groups.find(candidate => candidate.key === groupKeyOf(category)) || other;
            group.fields.push(item);
        });
        return other.fields.length > 0 ? [...groups, other] : groups;
    };

    const copyValue = (fieldPath, settingName) => {
        copy(form.getFieldValue(`${fieldPath}.settingValue`) ?? "");
        Notification.info({
            title: "Notification",
            content: `${settingName} Copied!`
        });
    };

    const renderItem = (item) => {
        const setting = form.getFieldValue(item.field);
        if (!setting) return null;
        const isSecret = SECRET_NAME_PATTERN.test(setting.settingName || "");
        const isTemplate = setting.category === SettingCategory.Templates;
        const isDirty = !!dirty[setting.id];
        const isRevealed = !!revealed[setting.id];
        const copyIcon = (
            <Tooltip content="Copy">
                <IconCopy
                    style={{ cursor: "pointer", color: "rgb(var(--gray-5))" }}
                    onClick={() => copyValue(item.field, setting.settingName)} />
            </Tooltip>
        );
        const eyeIcon = isSecret ? (
            <Tooltip content={isRevealed ? "Hide" : "Show"}>
                {isRevealed
                    ? <IconEye
                        style={{ cursor: "pointer", color: "rgb(var(--gray-5))" }}
                        onClick={() => toggleReveal(setting.id)} />
                    : <IconEyeInvisible
                        style={{ cursor: "pointer", color: "rgb(var(--gray-5))" }}
                        onClick={() => toggleReveal(setting.id)} />}
            </Tooltip>
        ) : null;
        const undoIcon = isDirty ? (
            <Tooltip content="Restore saved value">
                <IconUndo
                    style={{ cursor: "pointer", color: "rgb(var(--orange-6))" }}
                    onClick={() => resetField(item.field, setting.id)} />
            </Tooltip>
        ) : null;
        const actions = [copyIcon, undoIcon].filter(Boolean);
        const suffixActions = [eyeIcon, ...actions].filter(Boolean);
        const label = (
            <Space size={6}>
                <span>{setting.settingName}</span>
                {isTemplate && actions}
                {isDirty && <Tag color="orange" size="small">Modified</Tag>}
            </Space>
        );

        // Template names are long, use vertical layout to keep the label on its own line without wrapping
        const layoutProps = isTemplate
            ? { layout: "vertical" }
            : { labelCol: { span: 6 }, wrapperCol: { span: 18 } };

        return (
            <FormItem
                key={item.key}
                {...layoutProps}
                style={{ width: 860 }}
                label={label}
                field={`${item.field}.settingValue`}>
                {isTemplate
                    ? <Input.TextArea autoSize={{ minRows: 4, maxRows: 16 }} style={{ fontFamily: MONOSPACE_FONT }} />
                    : isSecret
                        ? <Input type={isRevealed ? "text" : "password"} suffix={<Space size={8}>{suffixActions}</Space>} />
                        : <Input suffix={<Space size={8}>{actions}</Space>} />}
            </FormItem>
        );
    };

    return (
        <Card
            title={<><IconEyeInvisible style={{ marginRight: 5 }} />Settings</>}
            loading={loading}
            extra={
                <Space>
                    {hasDirty && <Tag color="orange">Unsaved Changes</Tag>}
                    <Button disabled={!hasDirty} icon={<IconUndo />} onClick={resetAll}>Reset</Button>
                    <Button type="primary" loading={saving} disabled={!hasDirty} onClick={save}>Save</Button>
                </Space>
            }>
            <Form form={form} colon onValuesChange={refreshDirty}>
                <FormList field="settings">
                    {(fields) => {
                        const groups = buildGroups(fields);
                        return (
                            <Tabs activeTab={activeTab} onChange={setActiveTab} type="card-gutter">
                                {groups.map(group => {
                                    const dirtyCount = group.fields
                                        .filter(item => dirty[form.getFieldValue(`${item.field}.id`)]).length;
                                    return (
                                        <TabPane
                                            key={group.key}
                                            title={
                                                <Space size={6}>
                                                    {group.label}
                                                    {dirtyCount > 0 && <Badge count={dirtyCount} size="small" />}
                                                </Space>
                                            }>
                                                {/* Tabs content has no inner padding by default, add one so content does not stick to the tab border */}
                                                <div style={{ padding: "0 16px 16px" }}>
                                                    <Space direction="vertical" style={{ width: "100%" }}>
                                                        {group.fields.map(renderItem)}
                                                    </Space>
                                                </div>
                                            </TabPane>
                                    );
                                })}
                            </Tabs>
                        );
                    }}
                </FormList>
            </Form>
        </Card>
    );
}
